import type { CSSProperties } from "react";

/** Set de iconos de línea (stroke) usados en toda la app. Inline SVG, sin dependencias. */
export type IconName =
  | "brain" | "product" | "marketplace" | "inventory" | "sales" | "pricing"
  | "purchase" | "supplier" | "logistics" | "customer" | "analytics" | "finance"
  | "ai" | "search" | "bell" | "sun" | "moon" | "arrow-up" | "arrow-down"
  | "spark" | "shield" | "pulse" | "check" | "alert" | "target" | "bolt" | "dot" | "flask" | "copy";

const PATHS: Record<IconName, string> = {
  brain: "M12 3a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 6 0 3 3 0 0 0 2-5 3 3 0 0 0-2-5 3 3 0 0 0-3-3ZM12 3v18",
  product: "M3 8l9-5 9 5-9 5-9-5Zm0 0v8l9 5 9-5V8",
  marketplace: "M4 9h16l-1 11H5L4 9Zm4 0V6a4 4 0 0 1 8 0v3",
  inventory: "M3 7l9-4 9 4v10l-9 4-9-4V7Zm9 4v10M3 7l9 4 9-4",
  sales: "M4 19V5m0 14h16M8 15l3-4 3 2 4-6",
  pricing: "M20 12V6a2 2 0 0 0-2-2h-6L3 13l8 8 9-9Zm-13-3h.01",
  purchase: "M6 6h15l-1.5 9h-12L6 6Zm0 0L5 3H2m5 18a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm11 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
  supplier: "M3 21V8l6-4 6 4v13M9 21v-6h4v6M15 12h6v9M18 12V9",
  logistics: "M1 6h13v10H1V6Zm13 3h4l3 3v4h-7V9ZM5.5 16a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm11 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z",
  customer: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0",
  analytics: "M4 20V4m4 16v-6m4 6V8m4 12v-9m4 9V6",
  finance: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  ai: "M12 4a2 2 0 1 0 0-.001M12 6v3m-4 3H5m14 0h-3m-6.5 4.5L7 20m10 0-1.5-2.5M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z",
  search: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm10 17-5-5",
  bell: "M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0",
  sun: "M12 4V2m0 20v-2m8-8h2M2 12h2m13.7-5.7 1.4-1.4M4.9 19.1l1.4-1.4m0-11.4L4.9 4.9m14.2 14.2-1.4-1.4M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z",
  moon: "M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z",
  "arrow-up": "M12 19V5m0 0-6 6m6-6 6 6",
  "arrow-down": "M12 5v14m0 0 6-6m-6 6-6-6",
  spark: "M12 3l2.2 6.3L21 12l-6.8 2.7L12 21l-2.2-6.3L3 12l6.8-2.7L12 3Z",
  shield: "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3Z",
  pulse: "M3 12h4l2-6 4 12 2-6h6",
  check: "M4 12l5 5L20 6",
  alert: "M12 3l10 18H2L12 3Zm0 7v4m0 3h.01",
  target: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 4a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm0 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
  bolt: "M13 2 4 14h7l-1 8 9-12h-7l1-8Z",
  dot: "M12 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
  flask: "M9 3h6M10 3v6l-5.2 8.3A2 2 0 0 0 6.5 21h11a2 2 0 0 0 1.7-3.7L14 9V3M8 14h8",
  copy: "M9 9h10v10H9V9Zm-4 6H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1",
};

export interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function Icon({ name, size = 20, strokeWidth = 1.7, className, style, title }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      <path d={PATHS[name]} />
    </svg>
  );
}
